import { ref, computed, toRaw, watch, type Ref } from 'vue'

import type { AnyCalculatorDefinition, CalculatorContext, FormErrors } from '@/types/calculator'
import { decodeInputs } from '@/calculators/shared/shareParams'
import { useAppStore } from '@/stores/app'

export function useCalculator(definition: AnyCalculatorDefinition) {
  type Inputs = Record<string, unknown>
  type Result = Record<string, unknown>

  const app = useAppStore()
  const inputs = ref<Inputs>({ ...definition.defaults }) as Ref<Inputs>
  const errors = ref<FormErrors>({})
  const touched = ref<Record<string, boolean>>({})
  const result = ref<Result | null>(null)
  const hasCalculated = ref(false)

  const context = computed<CalculatorContext>(() => ({
    taxYear: app.taxYear,
    currency: app.currency,
  }))

  function validate(): boolean {
    // toRaw: the definition may arrive via reactive props; calling schema
    // methods through a Vue proxy violates proxy invariants on zod internals.
    const parsed = toRaw(definition.schema).safeParse(toRaw(inputs.value))
    if (parsed.success) {
      errors.value = {}
      result.value = definition.calculate(parsed.data, context.value)
      hasCalculated.value = true
      return true
    }
    const fieldErrors: FormErrors = {}
    parsed.error.issues.forEach((issue) => {
      const path = (issue.path as (string | number)[]).join('.')
      if (!fieldErrors[path]) {
        fieldErrors[path] = issue.message
      }
    })
    errors.value = fieldErrors
    result.value = null
    return false
  }

  function setField(name: string, value: unknown) {
    inputs.value[name] = value
    touched.value[name] = true
    validate()
  }

  function reset() {
    inputs.value = { ...definition.defaults }
    errors.value = {}
    touched.value = {}
    result.value = null
    hasCalculated.value = false
  }

  // Prefill inputs from share-URL query params (e.g. ?grossPay=40000) so a
  // shared link renders the result immediately without marking fields touched.
  if (typeof window !== 'undefined') {
    const decoded = decodeInputs(definition.inputs, new URLSearchParams(window.location.search))
    if (Object.keys(decoded).length > 0) {
      Object.assign(inputs.value, decoded)
      validate()
    }
  }

  watch(
    inputs,
    () => {
      if (Object.keys(touched.value).length > 0) {
        validate()
      }
    },
    { deep: true }
  )

  return {
    inputs,
    errors,
    touched,
    result,
    hasCalculated,
    setField,
    reset,
    validate,
  }
}
